import angular, {noop} from 'angular';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	EventEmitter,
	Input,
	Output,
	ViewChild,
	Inject,
	ElementRef,
	ChangeDetectorRef,
	OnInit,
	input
} from '@angular/core';
import { ArrayDataSource } from '@angular/cdk/collections';
import { SessionListDataSource } from '@go-modules/services/session-list-datasource/session-list.datasource';
import { SessionMenuComponent } from '../session-menu/session-menu.component';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { MenuComponent } from '@go-modules/components/menus/menu/menu.component';
import { NgxSessionService } from '@go-modules/services/session/session.service';
import { SessionEditorPanelService, sessionEditorPanelServiceToken } from 'go-modules/session-editor-panel/session-editor-panel.service';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { sessionToken, Session as SessionModel } from 'go-modules/models/session/session.service';
import { VideoShareDialogService } from '@go-modules/components/dialogs/video-share-dialog/video-share-dialog.service';
import { FIRST_COMMENT_TOUR_CLASSES } from '@go-modules/services/go-pulse-tour/constants/first-comment-tour';
import { ViewStateDataSource } from '@go-modules/services/view-state-datasource/view-state.datasource';
import { Observable } from 'rxjs';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';
import { ActiveBrandService } from '@go-modules/services/active-brand';
import type { BrandImage } from '@go-modules/interfaces/brands/brand.interface';

type SessionInstance = ReturnType<typeof SessionModel>;
import { VIEWS } from '@go-modules/enums/views';

@Component({
	selector: 'session-table',
	templateUrl: './session-table.component.html',
	styleUrls: ['./session-table.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class SessionTableComponent implements OnInit {
	public FirstCommentTourClasses = FIRST_COMMENT_TOUR_CLASSES;

	@Output() public goToSession = new EventEmitter<SessionInstance>();
	@Output() public selectionToggled = new EventEmitter<SessionInstance>();
	@ViewChild('sessionMenu') public sessionMenu: SessionMenuComponent;
	@ViewChild(MenuComponent) public menuComponent: MenuComponent;
	@ViewChild('tableContainer', {read: ElementRef})
	public tableContainer: ElementRef;

	public displayColumns: string[];
	public selectedSession: SessionInstance | null = null;
	@Input() public submitMode = false;
	@Input() public allowSelection = true;
	@Input() public selectedSessionIds: ReadonlySet<number> | null = null;
	@Input() public isSubmittingSessions = false;
	public currentView$: Observable<VIEWS>;

	public readonly sessions = input<ReturnType<typeof SessionModel>[]>();

	public readonly dataSource = computed(() => {
		const sessions = this.sessions();
		return sessions ? new ArrayDataSource(sessions) : this.sessionListDataSource;
	});

	constructor (
		public sessionListDataSource: SessionListDataSource,
		public ngxSessionService: NgxSessionService,
		public viewStateDataSource: ViewStateDataSource,
		private cdr: ChangeDetectorRef,
		private videoShareDialogService: VideoShareDialogService,
		@Inject(selectedServiceToken) public selectedService: SelectedService,
		@Inject(userServiceToken) private userService: UserService,
		@Inject(sessionEditorPanelServiceToken) private sessionEditorPanel: SessionEditorPanelService,
		@Inject(sessionToken) private Session: ReturnType<typeof SessionModel>,
		private featureFlag: NgxFeatureFlagService,
		private activeBrandService: ActiveBrandService
	) {}

	public get spotLogo (): BrandImage {
		return this.activeBrandService.getSpotLogo();
	}

	public ngOnInit () {
		this.displayColumns = ['affordance', 'name', 'participants', 'comments', 'score', 'status', 'actions'];

		if (!this.shouldDisplayScoreColumn()) {
			const index = this.displayColumns.indexOf('score');
			if (index > -1) {
				this.displayColumns.splice(index, 1);
			}
		}
	}

	public shouldDisplayScoreColumn (): boolean {
		const group = this.selectedService.getGroup();
		const activity = this.selectedService.getActivity();
		return group.hasReviewerRole(true) || !activity.shouldHoldFeedback(group.hasPresenterRole());
	}

	public shouldDisplaySubmissionDate (session: SessionInstance): boolean {
		return this.ngxSessionService.shouldDisplaySubmissionDate(session);
	}

	public isSubmittedLate (session: SessionInstance): boolean {
		return this.ngxSessionService.isSubmittedLate(session);
	}

	public openMenu (event: MouseEvent, session: SessionInstance): void {
		event.stopPropagation();
		// open browser menu on shift right click
		if (event.button === 2 && event.shiftKey) {
			return;
		}
		if (!this.ngxSessionService.shouldDisplayOptionsMenu(session)) {
			return;
		}

		event.preventDefault();
		this.selectedSession = session;
		this.menuComponent.openMenu(event, this.tableContainer.nativeElement);
	}

	public unviewedState (session: SessionInstance): boolean {
		return !session.viewed;
	}

	public shareSession (session: SessionInstance): void {
		this.videoShareDialogService.open({
			orgSettings: this.selectedService.getOrg().org_settings,
			sessionId: session.session_id
		}).subscribe((res) => {
			if (res && (['invalidEmails', 'invalidUserNames', 'invalidEmailsAndUserNames'].includes(res.error) || res.openEditUserPanel)) {
				this.onEdit(session);
			}
		});
	}

	private onEdit (session: SessionInstance): void {
		const sessionCopy = angular.copy(session);

		this.sessionEditorPanel.open({
			session: sessionCopy,
			user: this.userService.currentUser,
			group: this.selectedService.getGroup(),
			activity: this.selectedService.getActivity(),
			activeTab: 1
		})
			.result
			.then((result) => {
				if (result) {
					const editedSession = this.Session.model(result);
					this.sessionListDataSource.editSession(editedSession);
					this.cdr.markForCheck();
				}
			})
			.catch(noop);
	}

	public isSessionSelected (session: SessionInstance): boolean {
		return !!this.selectedSessionIds?.has(session.session_id);
	}

	public shouldShowSelectionCheckbox (session: SessionInstance): boolean {
		const media = this.ngxSessionService.getMedia(session);
		return !!(
			this.submitMode &&
			this.activityShouldShowTasks() &&
			session?.isRecorded?.() &&
			media &&
			!media.isPending?.() &&
			(session?.isPosted?.() || this.allowSelection) ||
			(!this.allowSelection && this.isSubmittingSessions)
		);
	}

	public onSubmitCheckboxChange (session: SessionInstance, event: MatCheckboxChange): void {
		if (event.checked === this.isSessionSelected(session)) {
			return;
		}

		if (!this.allowSelection || this.isSubmittingSessions) {
			event.source.checked = this.isSessionSelected(session);
			return;
		}

		this.selectionToggled.emit(session);
	}

	public handleRowInteraction (session: SessionInstance, event: Event): void {
		if (this.submitMode && this.activityShouldShowTasks()) {
			event.preventDefault();
			event.stopPropagation();
			if (this.allowSelection && this.shouldShowSelectionCheckbox(session)) {
				this.selectionToggled.emit(session);
			}
			return;
		}

		this.goToSession.emit(session);
	}

	private activityShouldShowTasks (): boolean {
		const activity = this.selectedService.getActivity();
		return activity?.show_tasks === true || this.featureFlag.isAvailable('ACTIVITY_PREVIEW_EDITOR_DIALOG');
	}
}
